import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  Tooltip,
  Grid,
  Fade,
  Link,
  Popover,
  Button,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import PolicyIcon from '@mui/icons-material/Policy';
// import AttachMoneyIcon from '@mui/icons-material/AttachMoney'; // commented - Pricing quick link removed
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AOS from 'aos';
import 'aos/dist/aos.css';
const socialLinks = [
  {
    icon: <LinkedInIcon />,
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/bixbite-ai/',
    gradient: 'linear-gradient(135deg, #0077b5 0%, #00a0dc 100%)',
  },
  {
    icon: <TwitterIcon />,
    label: 'Twitter',
    href: 'https://twitter.com/bixbiteai',
    gradient: 'linear-gradient(135deg, #1da1f2 0%, #4fc3f7 100%)',
  },
  {
    icon: <FacebookIcon />,
    label: 'Facebook',
    href: 'https://facebook.com/bixbiteai',
    gradient: 'linear-gradient(135deg, #1877f2 0%, #42a5f5 100%)',
  },
  {
    icon: <GitHubIcon />,
    label: 'GitHub',
    href: 'https://github.com/bixbiteai',
    gradient: 'linear-gradient(135deg, #333 0%, #666 100%)',
  },
];

const Footer = () => {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
      disable: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  }, []);

  const platformOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://platform.bixbite.ai';

  const quickLinks = [
    { label: 'Privacy Policy', icon: <PolicyIcon />, path: '/privacy-policy' },
    // { label: 'Pricing', icon: <AttachMoneyIcon />, path: '/bixbite_pricing' }, // pricing removed
  ];

  const productLinks = [
    { label: 'Download', href: '#download' },
    { label: 'Why LoCoPilot', href: '#features' },
    { label: 'Models', href: '#models' },
    { label: 'Project Memory', href: '#memory' },
  ];

  const resourceLinks = [
    { label: 'Getting Started', href: '#getting-started' },
    { label: 'About Us', href: '#about' },
    { label: 'Contact Us', href: 'mailto:contact@bixbite.ai', mailto: true },
  ];

  const handleSectionLink = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const CONTACT_EMAIL = 'contact@bixbite.ai';
  const [contactAnchor, setContactAnchor] = React.useState(null);
  const handleContactOpen = (e) => setContactAnchor(e.currentTarget);
  const handleContactClose = () => setContactAnchor(null);

  const footerLinkSx = {
    color: 'rgba(255, 255, 255, 0.72)',
    fontSize: '0.9rem',
    cursor: 'pointer',
    width: 'fit-content',
    transition: 'all 0.25s ease',
    '&:hover': { color: '#80cbc4', transform: 'translateX(4px)' },
  };

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        bgcolor: '#004d40',
        color: 'white',
        pt: { xs: 6, md: 8 },
        pb: { xs: 4, md: 5 },
        px: { xs: 3, md: 6 },
        overflow: 'hidden',
      }}
    >
      {/* Background Decorations */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(0, 121, 107, 0.1)',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -50,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(77, 182, 172, 0.1)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={600}>
              <Box data-aos="fade-right">
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 28, color: '#4db6ac', mr: 1 }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #4db6ac 0%, #80cbc4 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Bixbite AI
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.7,
                    maxWidth: 300,
                    mb: 2.5,
                  }}
                >
                  LoCoPilot is our free, privacy-first AI code editor. Build the future of software with AI—locally, on your own machine.
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  {socialLinks.map((social) => (
                    <Tooltip key={social.label} title={social.label} arrow>
                      <IconButton
                        aria-label={social.label}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          width: 42,
                          height: 42,
                          background: 'rgba(255, 255, 255, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            background: social.gradient,
                            transform: 'translateY(-4px) scale(1.1)',
                            boxShadow: '0 8px 20px -8px rgba(0, 0, 0, 0.4)',
                          },
                          '& svg': { fontSize: 19 },
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            </Fade>
          </Grid>

          {/* Product Links */}
          <Grid item xs={6} md={2.5}>
            <Fade in timeout={800}>
              <Box data-aos="fade-up">
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#4db6ac' }}>Product</Typography>
                <Stack spacing={1.25}>
                  {productLinks.map((link) => (
                    <Typography key={link.label} component="a" onClick={() => handleSectionLink(link.href)} sx={footerLinkSx}>{link.label}</Typography>
                  ))}
                </Stack>
              </Box>
            </Fade>
          </Grid>

          {/* Resource Links */}
          <Grid item xs={6} md={2.5}>
            <Fade in timeout={900}>
              <Box data-aos="fade-up">
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#4db6ac' }}>Resources</Typography>
                <Stack spacing={1.25}>
                  {resourceLinks.map((link) => (
                    link.mailto ? (
                      <Typography key={link.label} component="a" onClick={handleContactOpen} sx={footerLinkSx}>{link.label}</Typography>
                    ) : (
                      <Typography key={link.label} component="a" onClick={() => handleSectionLink(link.href)} sx={footerLinkSx}>{link.label}</Typography>
                    )
                  ))}
                </Stack>
              </Box>
            </Fade>
          </Grid>

          {/* Legal Links */}
          <Grid item xs={12} md={3}>
            <Fade in timeout={1000}>
              <Box data-aos="fade-left">
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#4db6ac' }}>Legal</Typography>
                <Stack spacing={1.5}>
                  {quickLinks.map((link) => (
                    <Link
                      key={link.label}
                      component="a"
                      href={`${platformOrigin}${link.path}`}
                      underline="none"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: '10px',
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        color: 'inherit',
                        width: 'fit-content',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ color: '#4db6ac', display: 'flex' }}>
                        {React.cloneElement(link.icon, { sx: { fontSize: 18 } })}
                      </Box>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {link.label}
                      </Typography>
                    </Link>
                  ))}
                </Stack>
              </Box>
            </Fade>
          </Grid>
        </Grid>

        <Divider
          sx={{ 
            my: 4, 
            borderColor: 'rgba(255, 255, 255, 0.1)',
          }} 
        />

        {/* Copyright */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            &copy; {new Date().getFullYear()} Bixbite AI. All rights reserved. Made with
            <FavoriteIcon sx={{ fontSize: 14, color: '#ef5350', mx: 0.5 }} />
          </Typography>
        </Box>
      </Box>

      {/* Contact Us popover — shows the email and a button to compose a message */}
      <Popover
        open={Boolean(contactAnchor)}
        anchorEl={contactAnchor}
        onClose={handleContactClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              mt: -1,
              p: 2,
              borderRadius: '14px',
              bgcolor: '#ffffff',
              color: '#00332b',
              border: '1px solid rgba(0, 121, 107, 0.2)',
              boxShadow: '0 16px 40px -12px rgba(0, 0, 0, 0.35)',
              maxWidth: 260,
            },
          },
        }}
      >
        <Stack spacing={1.5} alignItems="stretch">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: 18, color: '#00796b', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>{CONTACT_EMAIL}</Typography>
          </Box>
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
            href={`mailto:${CONTACT_EMAIL}`}
            onClick={handleContactClose}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, bgcolor: '#4db6ac', color: '#00332b', boxShadow: 'none', '&:hover': { bgcolor: '#80cbc4', boxShadow: 'none' } }}
          >
            Send us an email
          </Button>
        </Stack>
      </Popover>
    </Box>
  );
};

export default Footer;
